import { EMPTY, finalize, map, merge, mergeMap, Observable, of } from 'rxjs';
import { Pub, State, Sub } from '../../../../../../shared/state/state.abstract.ts';
import { orderService } from '../../../../services/order.service.ts';
import {
  buildComparisonKey,
  getDefaultComparisonDate,
  IComparisonDateRequest,
  IComparisonDateResult,
  IComparisonMetricsByKey,
  MAX_COMPARISON_DATES,
  TComparisonFailedDates,
} from './comparison.types.ts';

export interface IComparisonMetricsState {
  action:
    | 'toggleVisible'
    | 'addDate'
    | 'changeDate'
    | 'removeDate'
    | 'retryFailedDates'
    | 'setContext'
    | 'fetched'
    | 'reset'
    | undefined;
  visible: boolean;
  dates: string[];
  metricsByKey: IComparisonMetricsByKey;
  failedDates: TComparisonFailedDates;
  customerId?: string;
  orderDate?: string;
  articleIds: string[];
}

export const initComparisonMetricsState: IComparisonMetricsState = {
  action: undefined,
  visible: false,
  dates: [],
  metricsByKey: {},
  failedDates: {},
  customerId: undefined,
  orderDate: undefined,
  articleIds: [],
};

const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');

const withoutDates = (failedDates: TComparisonFailedDates, dates: string[]) =>
  Object.fromEntries(Object.entries(failedDates).filter(([date]) => !dates.includes(date)));

class PubImpl extends Pub<IComparisonMetricsState> {
  setContext(params: { customerId?: string; orderDate?: string; articleIds: string[] }) {
    const s = this.stream$.getValue();
    const ctxChanged = s.customerId !== params.customerId || s.orderDate !== params.orderDate;
    const idsChanged = !sameIds(s.articleIds, params.articleIds);
    if (!ctxChanged && !idsChanged) return;
    // a different customer invalidates the keyed metrics, so drop them up front
    const sameCustomer = s.customerId === params.customerId;
    const metricsByKey = sameCustomer ? s.metricsByKey : {};
    const failedDates = sameCustomer ? s.failedDates : {};
    this.emit('setContext', { ...params, metricsByKey, failedDates });
  }
  toggleVisible() {
    const s = this.stream$.getValue();
    const visible = !s.visible;
    let dates = s.dates;
    if (visible && !dates.length) {
      const def = getDefaultComparisonDate(s.orderDate);
      if (def) dates = [def];
    }
    this.emit('toggleVisible', { visible, dates });
  }
  addDate(date?: string) {
    const s = this.stream$.getValue();
    if (s.dates.length >= MAX_COMPARISON_DATES) return;
    const newDate = date || getDefaultComparisonDate(s.orderDate);
    if (!newDate || s.dates.includes(newDate)) return;
    this.emit('addDate', { dates: [...s.dates, newDate], visible: true });
  }
  changeDate(oldDate: string, newDate: string) {
    const s = this.stream$.getValue();
    if (s.dates.includes(newDate)) return;
    this.emit('changeDate', {
      dates: s.dates.map((d) => (d === oldDate ? newDate : d)),
      failedDates: withoutDates(s.failedDates, [oldDate]),
    });
  }
  removeDate(date: string) {
    const s = this.stream$.getValue();
    const dates = s.dates.filter((d) => d !== date);
    // collapse back to the off-state once the last date is removed
    this.emit('removeDate', {
      dates,
      visible: dates.length > 0 && s.visible,
      failedDates: withoutDates(s.failedDates, [date]),
    });
  }
  retryFailedDates() {
    const s = this.stream$.getValue();
    if (!Object.keys(s.failedDates).length) return;
    this.emit('retryFailedDates', { failedDates: {} });
  }
  reset() {
    this.emit('reset', { ...initComparisonMetricsState });
  }
}

class SubImpl extends Sub<IComparisonMetricsState> {
  private inFlightKeys = new Set<string>();

  protected actionHandlers(): Observable<IComparisonMetricsState> {
    return merge(this.passThrough(), this.fetchHandler());
  }
  private passThrough(): Observable<IComparisonMetricsState> {
    return this.actionListener([
      'toggleVisible',
      'addDate',
      'changeDate',
      'removeDate',
      'retryFailedDates',
      'setContext',
      'reset',
    ]);
  }
  private fetchHandler(): Observable<IComparisonMetricsState> {
    return this.actionListener([
      'toggleVisible',
      'addDate',
      'changeDate',
      'removeDate',
      'retryFailedDates',
      'setContext',
    ]).pipe(
      mergeMap((state) => {
        const { visible, dates, customerId, articleIds } = state;
        const shouldFetch = visible && dates.length > 0 && !!customerId && articleIds.length > 0;
        if (!shouldFetch) {
          // nothing to show — drop any leftover metrics from a previous context
          if (!Object.keys(this.stream$.getValue().metricsByKey).length) return EMPTY;
          const cleared: IComparisonMetricsState = {
            ...this.stream$.getValue(),
            metricsByKey: {},
            failedDates: {},
            action: 'fetched',
          };
          this.stream$.next(cleared);
          return of(cleared);
        }
        const requests = this.missingRequests(state);
        if (!requests.length) return EMPTY;
        const requestedKeys = requests.flatMap(({ comparisonDate, articleIds: ids }) =>
          ids.map((id) => buildComparisonKey(customerId, comparisonDate, id)),
        );
        requestedKeys.forEach((key) => this.inFlightKeys.add(key));
        orderService.pub.comparisonMetrics({ customerId: customerId!, requests });
        return orderService.sub.comparisonMetrics().pipe(
          map((result) => this.applyDateResult(result)),
          finalize(() => requestedKeys.forEach((key) => this.inFlightKeys.delete(key))),
        );
      }),
    );
  }
  private missingRequests({
    dates,
    customerId,
    articleIds,
    metricsByKey,
    failedDates,
  }: IComparisonMetricsState): IComparisonDateRequest[] {
    return dates
      .filter((date) => !failedDates[date])
      .map((comparisonDate) => ({
        comparisonDate,
        articleIds: articleIds.filter((id) => {
          const key = buildComparisonKey(customerId, comparisonDate, id);
          return !(key in metricsByKey) && !this.inFlightKeys.has(key);
        }),
      }))
      .filter(({ articleIds: ids }) => ids.length > 0);
  }
  private applyDateResult(result: IComparisonDateResult): IComparisonMetricsState {
    const { customerId, comparisonDate, articleIds, byKey, failure } = result;
    articleIds.forEach((id) =>
      this.inFlightKeys.delete(buildComparisonKey(customerId, comparisonDate, id)),
    );
    const current = this.stream$.getValue();
    const failureStillRelevant =
      failure && current.customerId === customerId && current.dates.includes(comparisonDate);
    const next: IComparisonMetricsState = {
      ...current,
      metricsByKey: { ...current.metricsByKey, ...byKey },
      failedDates: failureStillRelevant
        ? { ...current.failedDates, [comparisonDate]: failure }
        : current.failedDates,
      action: 'fetched',
    };
    this.stream$.next(next);
    return next;
  }
}

class ComparisonMetricsState extends State<IComparisonMetricsState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const comparisonMetricsState = new ComparisonMetricsState(initComparisonMetricsState);
